import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { getUserColorClass } from '@/components/ui/UserBadge/utils';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';
import { FormattedRelativeTime } from 'react-intl';
import { formatAbsolute, formatDelta, formatRelative } from './formatTimestamp';
import { getRelativeTimeValue } from './getRelativeTimeValue';
import {
  useTimelineDateMode,
  type TimelineDateMode,
} from './useTimelineDateMode';

interface TimelineEntryProps {
  /** Timestamp for the event, displayed as relative time on the right */
  timestamp: Date;
  /** Timestamp of the previous entry in the timeline (used for delta mode) */
  previousTimestamp?: Date;
  /** User who performed the event -- used for avatar and dot color */
  user: { recnum: number; name?: string; loginname?: string; avatar?: string };
  /** Whether this entry starts a new user+day group (shows avatar instead of dot) */
  isGroupStart: boolean;
  /** Whether this is the last entry in the timeline (controls line height) */
  isLastEvent: boolean;
  /** Icon and description displayed in the event header */
  icon: ReactNode;
  /** Description text/element displayed next to the icon */
  description: ReactNode;
  /** Optional actions (e.g. edit/delete dropdown) displayed to the left of the timestamp */
  eventActions?: ReactNode;
  /** Event-specific content rendered below the header */
  children?: ReactNode;
  /** Additional CSS class for the outer <li> */
  className?: string;
  /** Additional CSS class for the content wrapper (the right-side area) */
  contentClassName?: string;
}

/**
 * Build the display text and tooltip lines for the current mode.
 */
function getDisplayText(
  mode: TimelineDateMode,
  timestamp: Date,
  previousTimestamp: Date | undefined,
  relativeNode: ReactNode,
): { text: ReactNode; tooltipLines: [string, string] } {
  const absoluteStr = formatAbsolute(timestamp);
  const deltaStr = formatDelta(previousTimestamp ?? new Date(), timestamp);
  const relativeStr = formatRelative(timestamp);

  switch (mode) {
    case 'relative':
      return {
        text: relativeNode,
        tooltipLines: [absoluteStr, deltaStr],
      };
    case 'absolute':
      return {
        text: absoluteStr,
        tooltipLines: [relativeStr, deltaStr],
      };
    case 'delta':
      return {
        text: deltaStr,
        tooltipLines: [relativeStr, absoluteStr],
      };
  }
}

export function TimelineEntry({
  timestamp,
  previousTimestamp,
  user,
  isGroupStart,
  isLastEvent,
  icon,
  description,
  eventActions,
  children,
  className,
  contentClassName,
}: TimelineEntryProps) {
  const { value, unit } = getRelativeTimeValue(timestamp);
  const { mode, cycleMode } = useTimelineDateMode();

  const relativeNode = <FormattedRelativeTime value={value} unit={unit} />;
  const { text, tooltipLines } = getDisplayText(
    mode,
    timestamp,
    previousTimestamp,
    relativeNode,
  );

  return (
    <li className={cn('group/event relative flex gap-x-2', className)}>
      {/* Timeline line. Bottom segment is driven by :last-child so the
          connecting line stays correct regardless of display order. */}
      <div
        className={cn(
          'absolute top-0 left-0 flex w-6 justify-center',
          '-bottom-6 group-last/event:bottom-auto group-last/event:h-6',
          isLastEvent && 'bottom-auto h-6',
        )}
      >
        <div className="bg-border w-px" />
      </div>

      {/* Timeline bullet - user avatar for first event, small dot for subsequent */}
      {isGroupStart ? (
        <div
          className={cn(
            'bg-card relative flex flex-none items-center justify-center',
            '-mt-1 -mb-1 h-8 w-6',
          )}
        >
          <UserAvatar {...user} size="sm" />
        </div>
      ) : (
        <div className="bg-card relative flex size-6 flex-none items-center justify-center">
          <div
            className={cn(
              'size-1.5 rounded-full',
              getUserColorClass(user.recnum),
            )}
          />
        </div>
      )}

      {/* Event content */}
      <div className={cn('min-w-0 flex-auto', contentClassName)}>
        <div className="flex flex-col">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="text-muted-foreground flex items-center gap-2 text-xs/6">
                {icon}
                {description}
              </div>
            </div>

            {/* Right-aligned timestamp and actions */}
            <div className="ml-4 flex items-center gap-2">
              {eventActions}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="text-muted-foreground cursor-pointer text-xs select-none"
                    onClick={cycleMode}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        cycleMode();
                      }
                    }}
                  >
                    {text}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" sideOffset={4}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span>{tooltipLines[0]}</span>
                    <span>{tooltipLines[1]}</span>
                    <span className="text-primary-foreground/60 text-[10px]">
                      Click to change
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          {/* Event specific content */}
          {children}
        </div>
      </div>
    </li>
  );
}
